import { useI18n } from "../../../../hooks/i18n-hooks";
import ScreenCard from "../../../ScreenCard";
import { Linking, Platform, Pressable, StyleSheet, View } from "react-native";
import * as Notifications from "expo-notifications";
import React, { useCallback, useMemo } from "react";
import { WelcomeScreenProps } from "../Welcome";
import { useTheme } from "../../../../hooks/use-theme";
import * as Device from "expo-device";
import Text from "../../../../components/Text";
import { LanguageSelector } from "./LanguageSelector";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import WelcomeBubbleSvg from "../../../../assets/images/welcome-bubble.svg";
import VStack from "../../../../ui_atoms/VStack";
import LogoImage from "../../../../assets/images/logo.svg";
import { BodySmallText, TitleLargeText } from "../../../../components/FormattedText";
import Gap from "../../../../ui_atoms/Gap";
import AutoSizeImage from "../../../../components/AutoSizeImage";
import HStack from "../../../../ui_atoms/HStack";

const PARTNER_LOGO_HEIGHT = 20;

const OurPartnersComponent = () => {
    const i18n = useI18n();

    return (
        <VStack gap="2/3">
            <BodySmallText>
                {i18n.t("screens.auth.presentation_carousel.our_partners")}
            </BodySmallText>
            <HStack gap={1}>
                <AutoSizeImage
                    style={{ transform: [{ translateY: -3.5 }] }}
                    source={require("../../../../assets/images/centre_patronal.png")}
                    fixedHeight={PARTNER_LOGO_HEIGHT}
                />
                <AutoSizeImage
                    source={require("../../../../assets/images/suva.png")}
                    fixedHeight={PARTNER_LOGO_HEIGHT}
                    scale={0.6}
                />
                <AutoSizeImage
                    source={require("../../../../assets/images/gastrosocial.png")}
                    fixedHeight={PARTNER_LOGO_HEIGHT}
                    scale={0.8}
                />
            </HStack>
        </VStack>
    );
};

export const SmallLayout = ({ navigation }: WelcomeScreenProps) => {
    const i18n = useI18n();
    const styles = useStyles();
    const insets = useSafeAreaInsets();
    const goToPhoneNumberOrAskNotifications = useCallback(async () => {
        if (Platform.OS === "ios") {
            await Notifications.requestPermissionsAsync({
                ios: {
                    allowProvisional: true,
                },
            });
        }

        navigation.replace("SignupSigninStack");
    }, [navigation]);

    const goToSignup = useCallback(() => {
        navigation.replace("SignupSigninStack", { isSignup: true });
    }, [navigation]);

    const toAppStore = useCallback(async () => {
        if (Device.osName === "Android") {
            await Linking.openURL(
                "https://play.google.com/store/apps/details?id=ch.liftapp.app_production",
            );
        } else if (Device.osName === "iOS") {
            await Linking.openURL(
                "https://apps.apple.com/ch/app/lift-partenaire-professionnel/id6471666424",
            );
        }
    }, []);

    return (
        <ScreenCard
            isContentContainerFlex
            contentStyle={{ paddingBottom: 0, overflow: "visible" }}
            scrollEnabled={false}
            showsVerticalScrollIndicator={false}
            noBottomBlur
            buttons={[
                {
                    title: i18n.t("screens.auth.welcome.signup"),
                    onPress: goToSignup,
                    mode: "secondary",
                },
                {
                    title: i18n.t("screens.auth.welcome.login"),
                    onPress: goToPhoneNumberOrAskNotifications,
                    mode: "large-link",
                    textSize: "large",
                    size: "medium",
                    style: {
                        borderWidth: 0,
                        backgroundColor: "transparent",
                        marginTop: -6,
                        marginBottom: -20,
                    },
                },
            ]}
        >
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore */}
            {(screenActionsHeight) => (
                <View
                    style={{
                        flex: 1,
                    }}
                >
                    <WelcomeBubbleSvg
                        width="100%"
                        height="60%"
                        style={{ position: "absolute", opacity: 1 }}
                        preserveAspectRatio="none"
                    />
                    {Platform.OS === "web" && (
                        <>
                            <Pressable style={styles.appBanner} onPress={() => void toAppStore()}>
                                <Text style={styles.bannerText}>
                                    {i18n.t("screens.auth.welcome.download_app")}
                                </Text>
                            </Pressable>
                            <Gap gap={1} />
                        </>
                    )}
                    <VStack
                        alignment="center"
                        h={1}
                        gap={1}
                        style={{
                            flex: 1,
                            paddingTop: insets.top,
                            paddingBottom: screenActionsHeight,
                        }}
                    >
                        <LanguageSelector />
                        <Gap gap={1 / 2} />
                        <LogoImage height={76.24} width={104.24} />
                        <TitleLargeText>
                            {i18n.t("screens.auth.presentation_carousel.step_0.title")}
                        </TitleLargeText>
                        <Gap grow />
                        <OurPartnersComponent />
                    </VStack>
                </View>
            )}
        </ScreenCard>
    );
};

const useStyles = () => {
    const { colors, fonts } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                appBanner: {
                    backgroundColor: colors.onPrimary,
                    width: "100%",
                    height: 39,
                    alignItems: "center",
                    justifyContent: "center",
                },
                languageSelectorContainer: {
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    alignItems: "center",
                    zIndex: 10,
                },
                bannerText: {
                    ...fonts.bodyMedium,
                    fontSize: 13,
                    color: colors.secondary,
                },
            }),
        [colors.onPrimary, colors.secondary, fonts.bodyMedium],
    );
};
